import { useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useNavigate } from "react-router-dom";
import { Building2, LogIn, UserRound, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export default function Login() {
  const navigate = useNavigate();
  useEffect(() => {
    // If already authenticated, jump to dashboard
    axios.get(`${API}/auth/me`, { withCredentials: true }).then(() => navigate("/", { replace: true })).catch(()=>{});
  }, []);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const memberLoginUrl = typeof window !== "undefined"
    ? `${window.location.origin}/uye/giris?src=qr`
    : "/uye/giris?src=qr";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/yp5b7tl5_yn4.webp"
          alt="Kaya Şehir Misland — Giriş"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-slate-900/50 to-slate-900/80" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-white/15 backdrop-blur flex items-center justify-center">
              <Building2 size={20} />
            </div>
            <span className="font-semibold tracking-tight text-lg">Ortaklık Yönetim Sistemi</span>
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold leading-[1.05] mb-4">
              Ortaklık<br/>Bilgi<br/>Paneli
            </h1>
            <p className="text-white/80 max-w-md text-sm">
              Hisse Sorgulama, Adres ve iletişim bilgilerini güncelle, dilekçe oluştur, hisse belgesi oluştur.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Company logos */}
          <div className="mb-10 space-y-5">
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/4mwtidfk_misas.png"
                 alt="MİSAŞ" className="h-10 object-contain" data-testid="logo-misas" />
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/4d24m9nu_misholding.webp"
                 alt="MİS HOLDİNG" className="h-10 object-contain" data-testid="logo-misholding" />
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_partner-registry-2/artifacts/azkn7y74_miskoop.png"
                 alt="MİS KOOP" className="h-10 object-contain" data-testid="logo-miskoop" />
          </div>

          <div className="mb-8">
            <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Giriş Yapın</div>
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-2">Hoş geldiniz</h2>
            <p className="text-slate-600 text-sm">Rolünüze uygun giriş yöntemini seçin.</p>
          </div>

          <div className="space-y-3">
            <button
              data-testid="login-google-btn"
              onClick={handleGoogle}
              className="w-full inline-flex items-center justify-between gap-3 rounded-xl bg-slate-900 text-white font-semibold py-4 px-5 hover:bg-slate-800 active:translate-y-[1px] transition-transform shadow-md"
            >
              <span className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                  <LogIn size={18} />
                </span>
                <span className="text-left">
                  <span className="block text-base font-bold">Google ile Giriş Yap</span>
                  <span className="block text-[11px] text-white/70">Yönetim Paneli — yetkili personel</span>
                </span>
              </span>
              <span className="text-white/60 text-lg">→</span>
            </button>

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">veya</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Member entry: main button + adjacent QR code for mobile scan */}
            <div className="flex items-stretch gap-3">
              <a
                href="/uye/giris"
                data-testid="member-login-link"
                className="flex-1 inline-flex items-center justify-between gap-3 rounded-xl bg-white text-slate-900 font-semibold py-4 px-5 border-2 border-slate-900 hover:bg-slate-900 hover:text-white active:translate-y-[1px] transition-all shadow-sm group"
              >
                <span className="flex items-center gap-3 min-w-0">
                  <span className="h-9 w-9 rounded-full bg-slate-100 group-hover:bg-white/10 flex items-center justify-center transition-colors shrink-0">
                    <UserRound size={18} />
                  </span>
                  <span className="text-left min-w-0">
                    <span className="block text-base font-bold">Ortak Girişi</span>
                    <span className="block text-[11px] text-slate-500 group-hover:text-white/70 transition-colors truncate">T.C. kimlik + telefon ile</span>
                  </span>
                </span>
                <span className="text-slate-400 group-hover:text-white/70 transition-colors text-lg">→</span>
              </a>

              {/* QR code — visible on tablet/desktop; hidden on mobile since already on phone */}
              <div
                data-testid="member-qr"
                title="Telefonla okutun — cihaz kayıtlıysa bilgileriniz otomatik doldurulur"
                className="hidden sm:flex flex-col items-center justify-center rounded-xl border-2 border-slate-200 bg-white p-2 min-w-[92px]"
              >
                <QRCodeSVG value={memberLoginUrl} size={68} level="M" includeMargin={false} />
                <div className="mt-1 text-[9px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <Smartphone size={9}/> Karekod
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed pt-1 sm:pl-1">
              <span className="hidden sm:inline"><b>Karekodu telefonunuzla okutun:</b> giriş sayfası açılır ve bu cihazda daha önce giriş yaptıysanız <b>bilgileriniz otomatik doldurulur</b>.</span>
              <span className="sm:hidden">Ortak paneline giriş yapmak için düğmeye dokunun.</span>
            </p>
          </div>

          <div className="mt-8 text-xs text-slate-500">
            Yalnızca yetkili kullanıcılar erişebilir. Erişim kayıtları tutulur.
          </div>
        </div>
      </div>
    </div>
  );
}
